import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const PANEL_MAX_WIDTH = 280;
const VIEWPORT_MARGIN = 8;

/**
 * Toolbar dropdown with portaled menu (avoids scroll trap) and outside-click dismiss.
 */
const RequirementsToolbarMenu = ({
  label,
  isOpen,
  onOpenChange,
  menuId,
  children,
}) => {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const toggleId = useId();
  const [panelPosition, setPanelPosition] = useState(null);

  const updatePanelPosition = () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = Math.min(PANEL_MAX_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    let left = rect.left;
    if (left + panelWidth > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - VIEWPORT_MARGIN - panelWidth;
    }
    left = Math.max(VIEWPORT_MARGIN, left);
    setPanelPosition({
      top: rect.bottom + 4,
      left,
      width: panelWidth,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (
        wrapRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      onOpenChange(false);
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onOpenChange]);

  const toggle = () => onOpenChange(!isOpen);

  const menu =
    isOpen &&
    panelPosition &&
    createPortal(
      <div
        ref={menuRef}
        id={menuId}
        className="requirements-toolbar-menu-panel"
        role="menu"
        style={{
          position: 'fixed',
          top: panelPosition.top,
          left: panelPosition.left,
          width: panelPosition.width,
          zIndex: 1100,
        }}
      >
        {children}
      </div>,
      document.body
    );

  return (
    <div className="requirements-toolbar-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        id={toggleId}
        className="requirements-toolbar-menu-toggle"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? menuId : undefined}
      >
        {label}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`requirements-toolbar-menu-chevron${isOpen ? ' is-open' : ''}`}
        />
      </button>
      {menu}
    </div>
  );
};

RequirementsToolbarMenu.propTypes = {
  label: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  menuId: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default RequirementsToolbarMenu;
